import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Download,
  Share2,
  FileText,
  Calendar,
  DollarSign,
  Building,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileCheck,
  Eye,
} from 'lucide-react';
import ContractService from '../../services/contractService';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { jsPDF } from 'jspdf';

const ContractView = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { addToast, toasts, removeToast } = useToast(); 

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadContract();
  }, [contractId]);

  const loadContract = async () => {
    try {
      setLoading(true);
      const { data } = await ContractService.getContractById(contractId);
      setContract(data);
    } catch {
      setError('Failed to load contract data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
  try {
    setActionLoading(true);
    
    // 2. Initialize jsPDF
    const doc = new jsPDF();
    const margin = 20;
    let yPos = 20;

    // 3. Add Content (Styling the PDF)
    doc.setFontSize(22);
    doc.setTextColor(0, 123, 143); // Your primary color #007B8F
    doc.text('Contract Agreement', margin, yPos);
    
    yPos += 15;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos, 190, yPos); // Horizontal line
    
    yPos += 15;
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, yPos);

    yPos += 15;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('Basic Information', margin, yPos);
    
    yPos += 10;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    const info = [
      ['Title:', contract.title],
      ['Contract ID:', contractId],
      ['Counterparty:', contract.counterparty],
      ['Status:', contract.status],
      ['Type:', contract.type],
      ['Value:', `${contract.amount} ${contract.currency}`],
      ['Start Date:', formatDate(contract.startDate)],
      ['End Date:', formatDate(contract.endDate)],
    ];

    info.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, margin, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(String(value), margin + 40, yPos);
      yPos += 8;
    });

    yPos += 10;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.text('Description', margin, yPos);
    
    yPos += 8;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    // Split text into lines so it doesn't run off the page
    const splitDescription = doc.splitTextToSize(contract.description || 'No description provided.', 170);
    doc.text(splitDescription, margin, yPos);

    // 4. Trigger Download
    const fileName = `${contract.title.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);

    if (addToast) addToast('PDF Generated Successfully', 'success');
  } catch (err) {
    console.error('PDF Generation Error:', err);
    if (addToast) addToast('Failed to generate PDF', 'error');
  } finally {
    setActionLoading(false);
  }
};


  const handleShare = async () => {
    const shareData = {
      title: contract.title,
      text: `Check out the contract: ${contract.title}`,
      url: window.location.href,
    };

    try {
      // Use native browser share if available (Mobile / Modern Browsers)
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        if (addToast) addToast('Link copied to clipboard', 'success');
      }
    } catch (err) {
      // Ignore user aborting share, otherwise show error
      if (err.name !== 'AbortError' && addToast) {
        addToast('Failed to share', 'error');
      }
    }
  };

  const formatCurrency = (amount, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount
    );

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-3 h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-[#007B8F]" />
          <p className="text-sm text-gray-500">Loading contract…</p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <AlertCircle size={44} className="mx-auto mb-3 text-red-500" />
          <p className="mb-4 text-gray-500">{error || 'Contract not found'}</p>
          <button
            onClick={() => navigate('/contracts')}
            className="rounded-lg bg-[#007B8F] px-6 py-3 text-sm font-medium text-white hover:bg-[#006270]"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />

      {/* Header */}
      <div className="mb-6 flex items-center justify-between px-6">
        <div className="flex flex-col mt-6 ml-6">
          <h1 className="text-xl font-semibold text-gray-900 leading-tight">
            {contract.title}
          </h1>
          <p className="text-sm text-gray-500">
            {contractId} • {contract.status}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mr-6">
          
          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={actionLoading}
            className="flex items-center justify-center h-10 w-10 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition disabled:opacity-50"
            title="Download Contract"
          >
            <Download size={16} />
          </button>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="flex items-center justify-center h-10 w-10 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition"
            title="Share Contract"
          >
            <Share2 size={16} />
          </button>

          {/* Edit Button (Unchanged) */}
          <button
            onClick={() => navigate(`/contracts/edit/${contractId}`)}
            className="flex items-center gap-2 h-10 rounded-lg bg-[var(--primary-color)] px-5 text-sm font-medium text-white hover:bg-[#006270] transition"
          >
            <Edit size={16} />
            Edit
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-t-xl border-b border-gray-200 bg-white px-6 mx-8">
        <div className="flex">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'financial', label: 'Financial', icon: DollarSign },
            { id: 'workflow', label: 'Workflow', icon: FileCheck },
            { id: 'assets', label: 'Assets', icon: Building },
            { id: 'documents', label: 'Documents', icon: FileText },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center justify-center gap-2 px-5 py-4 text-sm font-medium transition ${
                activeTab === id
                  ? 'border-b-[3px] border-[var(--primary-color)] text-[var(--primary-color)]'
                  : 'border-b-[3px] border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="rounded-b-2xl bg-white p-8 shadow-sm mx-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-8">
            {[
              {
                title: 'Basic Information',
                icon: FileText,
                items: [
                  ['Contract Type', contract.type],
                  ['Counterparty', contract.counterparty],
                  ['Version', `v${contract.version}`],
                  ['Created', formatDate(contract.createdAt)],
                ],
              },
              {
                title: 'Dates & Duration',
                icon: Calendar,
                items: [
                  ['Start Date', formatDate(contract.startDate)],
                  ['End Date', formatDate(contract.endDate)],
                  ['Duration', `${contract.duration} days`],
                  contract.daysUntilExpiry !== null && [
                    'Days Until Expiry',
                    `${contract.daysUntilExpiry} days`,
                    contract.daysUntilExpiry <= 30,
                  ],
                ].filter(Boolean),
              },
            ].map(({ title, icon: Icon, items }, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-gray-200 bg-slate-50 p-6"
              >
                {/* Section header */}
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#007B8F]/10">
                    <Icon size={18} className="text-[#007B8F]" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-800">
                    {title}
                  </h3>
                </div>

                {/* Section content */}
                <div className="divide-y divide-gray-200">
                  {items.map(([label, value, danger], i) => (
                    <div
                      key={i}
                      className="flex items-start justify-between py-3"
                    >
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        {label}
                      </p>
                      <p
                        className={`
                          text-sm font-semibold text-right
                          ${danger ? 'text-red-500' : 'text-gray-800'}
                        `}
                      >
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="grid grid-cols-3 gap-6">
            {[
              [
                'Contract Amount',
                formatCurrency(contract.amount, contract.currency),
              ],
              ['Payment Terms', contract.paymentTerms],
              [
                'Total Value',
                formatCurrency(contract.totalValue, contract.currency),
              ],
            ].map(([label, value], i) => (
              <div key={i} className="rounded-xl border bg-slate-50 p-6">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {label}
                </p>
                <p className="mt-2 text-xl font-bold text-gray-800">{value}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'assets' &&
          (contract.linkedAssets?.length ? (
            <div className="grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
              {contract.linkedAssets.map((asset, i) => (
                <div key={i} className="rounded-xl border bg-slate-50 p-5">
                  <p className="font-semibold text-gray-800">{asset.name}</p>
                  <p className="text-sm text-gray-500">
                    Type: {asset.type || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Status: {asset.status || 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Building size={44} className="mx-auto mb-3 opacity-40" />
              No linked assets
            </div>
          ))}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ContractView;